import * as React from "react"
import { CartStructure, Product } from "../../interfaces/SaleorTypes"
import { AnimatePresence, motion } from "framer-motion"
import styled from "styled-components"
import H4 from "../../atoms/typography/H4"
import { HorizontalSpacer } from "../../atoms/spacers"
import { ArrowRight } from "../../atoms/icons/ArrowRight"
import { useNavigate } from "react-router-dom"
import { MediaQuery } from "../../../utils/design-helpers"
import BodyMedium from "../../atoms/typography/BodyMedium"
import { calculateSubtotal, getFormattedPrice } from "../../../utils/cart-helpers"
import H3 from "../../atoms/typography/H3"
import Line from "../../atoms/Line"
import { Row } from "react-grid-system"
import { CrossIcon } from "../../atoms/icons/CrossIcon"
import TextButton from "../../atoms/buttons/TextButton"
import { CartSolutionOrder } from "../../../utils/models"
import CartSolutionSection from "../../molecules/store/cart/CartSolutionSection"
import CartAccessorySection from "../../molecules/store/cart/CartAccessorySection"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"

interface Props {
  cart: CartStructure[]
  removeFromCart: (products: CartStructure[]) => void
  addToCart: (products: CartStructure[]) => void
  updateItemInCart: (item: Product) => void
  open: boolean
  onClose: () => void
  clearCart: () => void
  solutions: CartSolutionOrder[]
  removeSolution: (id: string) => void
}

const ModalContainer = styled.div`
  width: 40vw;
  height: 93vh;
  position: absolute;
  top: 16px;
  right: 0; //-30px;
  z-index: 12;
  visibility: visible;

  ${MediaQuery.XS} {
    width: 95vw;
    height: 80vh;
    top: 16px;
  }
`

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.white};
  padding: 1.5rem;
  border-left: ${(props) => `3px solid ${props.theme.gradients.blueBorder}`};
  overflow-y: scroll;
  position: relative;
  z-index: 12;
`

const CheckOutButtonContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: space-between;
  position: sticky;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  left: -1.5rem;
  bottom: -1.5rem;
  width: 100%;
  align-items: baseline;
`

const Cart: React.FC<Props> = ({
  cart,
  removeFromCart,
  addToCart,
  updateItemInCart,
  open,
  onClose,
  clearCart,
  solutions,
  removeSolution,
}) => {
  const navigate = useNavigate()
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  const terminals = cart.filter((item) => item.product.category.name == "Terminals")
  const accessories = cart.filter((item) => item.product.category.name == "Accessories")

  const onClickGoToCheckout = () => {
    navigate("/store/checkout")
    onClose()
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      onClose()
    }
  }

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const onClickClearCart = () => {
    clearCart()
  }

  return (
    <div style={{ zIndex: 15 }} ref={dropdownRef}>
      <div>
        <AnimatePresence>
          {open && (
            <motion.div
              style={{ position: "relative" }}
              key="modal"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <ModalContainer>
                <ModalContent>
                  <Row style={{ padding: "0 15px", alignItems: "center" }} justify="between">
                    <H4>My cart</H4>
                    <div onClick={onClose} style={{ cursor: "pointer", filter: "opacity: (0.6)" }}>
                      <CrossIcon />
                    </div>
                  </Row>
                  <HorizontalSpacer spacing={2} />
                  {terminals && terminals.length > 0 && (
                    <div>
                      <BodyMedium>Terminals</BodyMedium>
                      <Line />
                      <HorizontalSpacer spacing={1} />

                      <CartSolutionSection
                        solutions={solutions}
                        removeSolution={removeSolution}
                        removeFromCart={removeFromCart}
                        editable
                      />
                    </div>
                  )}

                  <HorizontalSpacer spacing={1} />
                  {accessories && accessories.length > 0 && (
                    <div>
                      <BodyMedium>Accessories</BodyMedium>
                      <Line />
                      <HorizontalSpacer spacing={1} />

                      {accessories.map((accessories: CartStructure, index: number) => (
                        <CartAccessorySection
                          key={`${accessories.product.id} ${index}`}
                          accessory={accessories}
                          addToCart={addToCart}
                          editable
                          removeFromCart={removeFromCart}
                        />
                      ))}
                    </div>
                  )}

                  <HorizontalSpacer spacing={1} />

                  <Row nogutter justify="center">
                    <TextButton onClick={onClickClearCart} title="Clear cart" />
                  </Row>
                  <HorizontalSpacer spacing={1} />

                  <CheckOutButtonContainer>
                    <H3>
                      {`Total: ${getFormattedPrice(
                        calculateSubtotal(cart),
                        cart[0]?.product.variants[0].pricing.price.currency,
                      )} + shipping`}
                    </H3>
                    <SmallColorfulButton
                      title="Checkout"
                      iconRight={<ArrowRight color="white" />}
                      onClick={onClickGoToCheckout}
                      disabled={cart?.length < 1}
                    />
                  </CheckOutButtonContainer>
                </ModalContent>
              </ModalContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Cart
